/**
 * Detect whether a PDF needs a user password to open.
 * Uses trailer `/Encrypt n n R` + Standard security handler — avoids loose
 * `/Encrypt` substring matches that flag many unprotected LMS/HR PDFs.
 */
export const isPdfFile = (file) =>
  Boolean(file) &&
  (String(file.type || "").toLowerCase() === "application/pdf" ||
    String(file.name || "")
      .toLowerCase()
      .endsWith(".pdf"));

const decodeLatin1 = (bytes, start, length) => {
  const end = Math.min(bytes.length, start + length);
  return new TextDecoder("latin1").decode(bytes.subarray(start, end));
};

export const isPdfPasswordProtected = async (file) => {
  if (!isPdfFile(file)) return false;

  try {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    if (bytes.length < 8) return false;

    const headLen = Math.min(bytes.length, 2 * 1024 * 1024);
    let text = decodeLatin1(bytes, 0, headLen);
    if (bytes.length > headLen) {
      const tailStart = Math.max(0, bytes.length - 512 * 1024);
      text += `\n${decodeLatin1(bytes, tailStart, bytes.length - tailStart)}`;
    }

    // Real encryption points at an Encrypt object from the trailer/xref.
    // Do NOT match bare "/Encrypt" (false positives on many openable PDFs).
    const hasEncryptRef = /\/Encrypt\s+\d+\s+\d+\s+R/.test(text);
    if (!hasEncryptRef) return false;

    // Password-based PDFs almost always use the Standard security handler.
    // If missing in the scanned window, treat as not requiring user password.
    return /\/Filter\s*\/Standard/.test(text);
  } catch {
    return false;
  }
};
